import { useCallback, useEffect, useRef, useState } from "react";
import { ChatConversation, ChatMessage } from "../types";
import { generateMessageId, generateConversationId } from "../utils/idGenerator";
import { CHAT_CONFIG, NO_PARENT } from "../constants";
import { postChat, subscribeChatStream, getGeneratedTitle } from "../../../api/dumontApi";
import { useDumont } from "../../../context/DumontContext";

const TEMP_NEW_ID = "new";

export type UseChatOptions = {
  initialMessages?: ChatMessage[];
  saveConversation: (conversation: ChatConversation) => Promise<string | null>;
  updateConversation: (conversationId: string, messages: ChatMessage[]) => Promise<boolean>;
  updateConversationTitle: (conversationId: string, title: string) => void;
  replaceConversationId: (oldId: string, newId: string) => void;
  /** Dumont agent id for postChat (e.g. from useAvailableAgents or defaultAgentId) */
  agentId?: string | null;
};

export const useChat = (options: UseChatOptions) => {
  const { initialMessages = [], saveConversation, updateConversation, updateConversationTitle, replaceConversationId, agentId } = options;
  const { baseUrl, token, defaultAgentId } = useDumont();

  const [messages, setMessages] = useState<ChatMessage[]>(initialMessages);
  const [inputText, setInputText] = useState("");
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [documentContext, setDocumentContext] = useState<string | null>(null);
  const [documentFileName, setDocumentFileName] = useState<string | null>(null);

  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const currentConversationIdRef = useRef<string | null>(null);
  const streamAbortRef = useRef<AbortController | null>(null);
  const streamingTextRef = useRef("");
  const isMountedRef = useRef(true);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    currentConversationIdRef.current = currentConversationId;
  }, [currentConversationId]);

  const sendMessage = useCallback(async () => {
    const text = inputText.trim();
    if (!text || !baseUrl || !token) return;
    if (!isMountedRef.current) return;

    const newMessage: ChatMessage = {
      id: generateMessageId(),
      text,
      sender: "user",
      timestamp: new Date(),
    };

    const updatedMessages = [...messages, newMessage];
    setMessages(updatedMessages);
    setInputText("");

    let conversationId = currentConversationId;

    if (!currentConversationId) {
      // Create new conversation in backend
      const title =
        text.length > CHAT_CONFIG.MAX_TITLE_LENGTH
          ? text.substring(0, CHAT_CONFIG.MAX_TITLE_LENGTH) + CHAT_CONFIG.TITLE_ELLIPSIS
          : text;

      const conversation: ChatConversation = {
        id: generateConversationId(),
        title,
        messages: updatedMessages,
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      const backendId = await saveConversation(conversation);
      if (backendId) {
        if (!isMountedRef.current) return;
        conversationId = backendId;
        setCurrentConversationId(backendId);
        currentConversationIdRef.current = backendId;
        console.log('🔵 [Dumont] Conversation created in DB with UUID:', backendId);
      }
    } else {
      await updateConversation(currentConversationId, updatedMessages);
    }

    if (!isMountedRef.current) return;
    setIsLoading(true);

    const loadingMessage: ChatMessage = {
      id: TEMP_NEW_ID,
      text: "",
      sender: "dumont",
      timestamp: new Date(),
      isLoading: true,
    };
    setMessages((prev) => [...prev, loadingMessage]);

    const convIdForRequest = currentConversationIdRef.current;
    const isNewConvo = convIdForRequest === TEMP_NEW_ID;
    const dumontConversationId = isNewConvo ? null : convIdForRequest;

    const agent_id = agentId ?? defaultAgentId ?? "default";
    
    try {
      streamAbortRef.current?.abort();
      streamingTextRef.current = "";

      const res = await postChat(baseUrl, token, {
        endpoint: "agents",
        agent_id,
        text,
        conversationId: dumontConversationId,
        parentMessageId: isNewConvo ? NO_PARENT : undefined,
      });

      if (!isMountedRef.current) return;

      if (res.conversationId && isNewConvo) {
        replaceConversationId(TEMP_NEW_ID, res.conversationId);
        setCurrentConversationId(res.conversationId);
        currentConversationIdRef.current = res.conversationId;

        const newConvId = res.conversationId;
        getGeneratedTitle(baseUrl, token, newConvId).then((title) => {
          if (title) updateConversationTitle(newConvId, title);
        });
      }
      
      const loadingId = loadingMessage.id;
      streamAbortRef.current = subscribeChatStream(
        baseUrl,
        token,
        res.streamId,
        (data: string | object) => {
          if (!isMountedRef.current) return;
          const obj = typeof data === "object" && data !== null ? (data as Record<string, unknown>) : null;
          if (obj && (obj as { final?: unknown }).final != null) {
            let finalText = streamingTextRef.current.trim();

            if (!finalText) {
              const respMsg = (obj as Record<string, unknown>).responseMessage as
                | { content?: Array<{ type?: string; error?: string; text?: string }> }
                | undefined;
              const errorPart = respMsg?.content?.find((p) => p.type === "error");
              if (errorPart?.error) {
                finalText = "Sorry, there was a problem processing your request. Please try again.";
                console.error("[Dumont] Generation error:", errorPart.error);
              }
            }
            if (finalText) {
              const assistantMessage: ChatMessage = {
                id: generateMessageId(),
                text: finalText,
                sender: "dumont",
                timestamp: new Date(),
              };
              setMessages((prev) => {
                if (!isMountedRef.current) return prev;
                const withoutLoading = prev.filter((m) => m.id !== loadingId);
                return [...withoutLoading, assistantMessage];
              });
              const convId = currentConversationIdRef.current;
              if (convId) {
                const finalMessages = [...updatedMessages, assistantMessage];
                updateConversation(convId, finalMessages);
              }
            } else {
              setMessages((prev) => {
                if (!isMountedRef.current) return prev;
                return prev.filter((m) => m.id !== loadingId);
              });
            }
            if (isMountedRef.current) setIsLoading(false);
            streamAbortRef.current = null;
            return;
          }
          const eventName = obj?.event as string | undefined;
          const eventData = obj?.data as { delta?: { content?: unknown } } | undefined;
          if (eventName === "on_message_delta" && eventData?.delta?.content) {
            const content = eventData.delta.content;
            const firstPart = Array.isArray(content) ? content[0] : content;
            const textPart =
              typeof (firstPart as { text?: string })?.text === "string"
                ? (firstPart as { text: string }).text
                : "";
            if (textPart) {
              streamingTextRef.current += textPart;
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === loadingId ? { ...m, text: streamingTextRef.current } : m
                )
              );
            }
            return;
          }
          if (typeof data === "string" && data) {
            streamingTextRef.current += data;
            setMessages((prev) =>
              prev.map((m) =>
                m.id === loadingId ? { ...m, text: streamingTextRef.current } : m
              )
            );
          }
        },
        () => {
          if (!isMountedRef.current) return;
          setMessages((prev) => {
            if (!isMountedRef.current) return prev;
            const withoutLoading = prev.filter((m) => m.id !== loadingId);
            return [
              ...withoutLoading,
              {
                id: generateMessageId(),
                text: "Sorry, there was a problem. Please try again.",
                sender: "dumont" as const,
                timestamp: new Date(),
              },
            ];
          });
          if (isMountedRef.current) setIsLoading(false);
          streamAbortRef.current = null;
        },
        () => {
          if (isMountedRef.current) setIsLoading(false);
          streamAbortRef.current = null;
        }
      );
    } catch (error) {
      console.error("Error sending message to Dumont:", error);
      if (!isMountedRef.current) return;
      setMessages((prev) => {
        const withoutLoading = prev.filter((m) => m.id !== loadingMessage.id);
        return [
          ...withoutLoading,
          {
            id: generateMessageId(),
            text: "Sorry, I'm having trouble connecting. Please try again later.",
            sender: "dumont" as const,
            timestamp: new Date(),
          },
        ];
      });
      const convId = currentConversationIdRef.current;
      if (convId) {
        const finalMessages = [...updatedMessages, {
          id: generateMessageId(),
          text: "Sorry, I'm having trouble connecting. Please try again later.",
          sender: "dumont" as const,
          timestamp: new Date(),
        }];
        updateConversation(convId, finalMessages);
      }
      if (isMountedRef.current) setIsLoading(false);
    }
  }, [
    inputText,
    messages,
    currentConversationId,
    baseUrl,
    token,
    defaultAgentId,
    agentId,
    saveConversation,
    updateConversation,
    updateConversationTitle,
    replaceConversationId,
  ]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      streamAbortRef.current?.abort();
    };
  }, []);

  const handleKeyPress = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        sendMessage();
      }
    },
    [sendMessage]
  );

  const resetChat = useCallback(() => {
    setMessages([]);
    setInputText("");
    setCurrentConversationId(null);
    setDocumentContext(null);
    setDocumentFileName(null);
  }, []);

  const handleFileUpload = useCallback((content: string, fileName: string) => {
    setDocumentContext(content);
    setDocumentFileName(fileName);
  }, []);

  const removeDocument = useCallback(() => {
    setDocumentContext(null);
    setDocumentFileName(null);
  }, []);

  return {
    messages,
    inputText,
    currentConversationId,
    messagesEndRef,
    isLoading,
    documentContext,
    documentFileName,
    setInputText,
    setMessages,
    setCurrentConversationId,
    sendMessage,
    handleKeyPress,
    resetChat,
    handleFileUpload,
    removeDocument,
  } as const;
};


